import { apiClient } from '@/api/client'
import type { ModifierGroup, ResolvedModifierGroup } from '@/types'
import {
  draftFromGroup,
  draftProblem as draftProblemOf,
  groupPayload,
  isSizeGroup,
  optionsPayload,
  ownSizeListName,
  type VariantDraft,
} from './variantShape'
import type { VariantTemplate } from './variantTemplates'

/**
 * The product form's variants state and how it is written (F-MOD-N12).
 *
 * The form thinks in two things: "does this item come in sizes" and "what is
 * the cashier asked". Under the hood both are modifier groups attached
 * directly or inherited from the category. This module owns that mapping so
 * the section component is only UI.
 */

export interface SizeState {
  /** Absent until the list is created on save. */
  groupId?: string
  /** Where the attachment comes from; 'new' is a list typed since the last save. */
  source: 'product' | 'category' | 'new'
  draft: VariantDraft
  /** Items asking this list, from the resolved catalogue. 1 = this item only. */
  sharedCount: number
  dirty: boolean
}

export type QuestionRow =
  | {
      kind: 'shared'
      groupId: string
      source: 'product' | 'category'
      /** Present while the owner has it open for editing. */
      draft?: VariantDraft
      dirty: boolean
    }
  | { kind: 'new'; tempKey: string; draft: VariantDraft }

export interface VariantsState {
  sizes: SizeState | null
  questions: QuestionRow[]
  /** Category lists turned off for this item only. */
  excluded: string[]
  /**
   * An own size list (asked on this item alone) the owner switched off. It is
   * deleted on save rather than left orphaned in Shared lists.
   */
  deleteGroupIds: string[]
}

export const EMPTY_VARIANTS: VariantsState = { sizes: null, questions: [], excluded: [], deleteGroupIds: [] }

let tempSeq = 0
export function nextTempKey(): string {
  tempSeq += 1
  return `new-${tempSeq}`
}

/** Build the state from what the server says the item asks today. */
export function stateFromServer(
  resolved: ResolvedModifierGroup[],
  excluded: string[],
  byId: Map<string, ModifierGroup>,
  usage: Map<string, number>,
): VariantsState {
  let sizes: SizeState | null = null
  const questions: QuestionRow[] = []
  for (const r of resolved) {
    const full = byId.get(r.id) ?? r
    if (isSizeGroup(full)) {
      if (!sizes) {
        sizes = {
          groupId: full.id,
          source: r.source,
          draft: draftFromGroup(full),
          sharedCount: usage.get(full.id) ?? 1,
          dirty: false,
        }
      }
      continue
    }
    questions.push({ kind: 'shared', groupId: full.id, source: r.source, dirty: false })
  }
  return { sizes, questions, excluded: [...excluded], deleteGroupIds: [] }
}

export function snapshot(s: VariantsState): string {
  return JSON.stringify(s)
}

/** A fresh, unsaved size list for this item. */
export function newOwnSizes(productName: string, from?: VariantDraft): SizeState {
  const base: VariantDraft = from
    ? { ...from, id: undefined, choices: from.choices.map((c) => ({ ...c, id: undefined })) }
    : {
        name: '',
        prompt: 'Which size?',
        kind: 'size',
        rule: { mode: 'exactly', n: 1, m: 1 },
        allowQuantity: false,
        maxPerOption: 1,
        isActive: true,
        sortOrder: 0,
        choices: [
          { name: 'Regular', price: 0, isDefault: true, isAvailable: true, groupRules: [] },
          { name: 'Large', price: 0, isDefault: false, isAvailable: true, groupRules: [] },
        ],
      }
  return {
    source: 'new',
    draft: { ...base, name: ownSizeListName(productName), kind: 'size' },
    sharedCount: 1,
    dirty: true,
  }
}

export function draftFromTemplate(t: VariantTemplate): VariantDraft {
  return {
    name: t.name,
    prompt: t.prompt,
    kind: t.kind,
    rule: t.rule,
    allowQuantity: false,
    maxPerOption: 1,
    isActive: true,
    sortOrder: 0,
    choices: t.choices.map((c) => ({
      name: c.name,
      price: c.price ?? 0,
      isDefault: Boolean(c.isDefault),
      isAvailable: true,
      groupRules: [],
    })),
  }
}

/** Why the state cannot be saved yet, or null. Checks sizes and every open draft. */
export function draftProblem(s: VariantsState): string | null {
  if (s.sizes) {
    const p = draftProblemOf(s.sizes.draft)
    if (p) return `Sizes: ${p}`
  }
  for (const q of s.questions) {
    const draft = q.kind === 'new' ? q.draft : q.draft
    if (!draft) continue
    const p = draftProblemOf(draft)
    if (p) return `${draft.name || 'New question'}: ${p}`
  }
  return null
}

/** The lowest size price, which becomes the product's listed price. */
export function basePriceFromSizes(s: SizeState | null): number | null {
  if (!s) return null
  const prices = s.draft.choices.filter((c) => c.name.trim() !== '').map((c) => Number(c.price) || 0)
  if (prices.length === 0) return null
  return Math.min(...prices)
}

async function createList(draft: VariantDraft, sortOrder: number): Promise<string> {
  const res = await apiClient.createModifierGroup({ ...groupPayload({ ...draft, sortOrder }) })
  if (!res.success || !res.data?.id) throw new Error(res.message || 'Could not create the list')
  const id = res.data.id
  const opt = await apiClient.replaceModifierOptions(id, optionsPayload(draft))
  if (!opt.success) throw new Error(opt.message || 'Could not save the choices')
  return id
}

async function updateList(draft: VariantDraft): Promise<void> {
  if (!draft.id) return
  const res = await apiClient.updateModifierGroup(draft.id, groupPayload(draft))
  if (!res.success) throw new Error(res.message || 'Could not save the list')
  const opt = await apiClient.replaceModifierOptions(draft.id, optionsPayload(draft))
  if (!opt.success) throw new Error(opt.message || 'Could not save the choices')
}

/**
 * Write everything the section changed, in dependency order: lists first
 * (so new ones have ids), then the attachment, then deletes. Throws on the
 * first failure with a message the form can show; partial progress is safe
 * because every step is idempotent on retry.
 */
export async function persistVariants(productId: string, productName: string, s: VariantsState): Promise<void> {
  const direct: string[] = []
  let sort = 0

  if (s.sizes) {
    const draft = { ...s.sizes.draft, name: s.sizes.draft.name.trim() || ownSizeListName(productName) }
    if (s.sizes.source === 'new') {
      direct.push(await createList(draft, sort))
    } else {
      if (s.sizes.dirty) await updateList(draft)
      if (s.sizes.source === 'product' && s.sizes.groupId) direct.push(s.sizes.groupId)
    }
    sort += 10
  }

  for (const q of s.questions) {
    if (q.kind === 'new') {
      direct.push(await createList(q.draft, sort))
    } else {
      if (q.draft && q.dirty) await updateList(q.draft)
      if (q.source === 'product') direct.push(q.groupId)
    }
    sort += 10
  }

  const attach = await apiClient.setProductModifierGroups(productId, {
    group_ids: direct,
    excluded_category_group_ids: s.excluded,
  })
  if (!attach.success) throw new Error(attach.message || 'Could not attach the lists')

  for (const id of s.deleteGroupIds) {
    const res = await apiClient.deleteModifierGroup(id)
    if (!res.success) throw new Error(res.message || 'Could not remove the old size list')
  }
}
